import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import { globalClassFilterClasses } from '../graphQLOperations/Classes';
import { ClassesGlobalFilterQuery } from '../graphQLOperations/__generated__/ClassesGlobalFilterQuery.graphql';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { selectUser } from '../redux/reducers/userSlice';
import {
  hydrateSelectedClassIds,
  selectClassesVersion,
  selectGlobalClassFilterHydrated,
  selectSelectedClassIds,
  setSelectedClassIds,
} from '../redux/reducers/globalClassFilterSlice';
import { readStoredIds, writeStoredIds } from '../businessLogicService/globalClassFilterStorage';
import { selectedTeacherDashboardFilter } from '../redux/reducers/teacherDashboardFiltersSlice';
import { reconcileGlobalClassFilter } from '../businessLogicService/reconcileGlobalClassFilter';
import { useHiddenClassIds } from './useHiddenClassIds';
import { useClassIcons } from './useClassIcons';
import { resolveClassIcon } from '../businessLogicService/resolveClassIcon';
import { CLASS_ICON_NAMES } from '../components/ClassIcon/classIcons';

export interface GlobalFilterClass {
  id: string;
  name: string;
  section: string | null;
  studentCount: number;
  icon: string | null;
}

export interface GlobalClassFilter {
  classes: GlobalFilterClass[];
  selectedClassIds: string[];
  selectedClasses: GlobalFilterClass[];
  selectedStudentCount: number;
  select: (classIds: string[]) => void;
}

export const useGlobalClassFilter = (skip = false): GlobalClassFilter => {
  const dispatch = useAppDispatch();
  const userRedux = useAppSelector(selectUser);
  const publicUserId = userRedux?.sub ?? '';
  const selectedClassIds = useAppSelector(selectSelectedClassIds);
  const isHydrated = useAppSelector(selectGlobalClassFilterHydrated);
  const classesVersion = useAppSelector(selectClassesVersion);

  const dashboardClassIds = useAppSelector(selectedTeacherDashboardFilter).classes;
  const dashboardClassIdsRef = useRef<string[]>([]);
  dashboardClassIdsRef.current = (dashboardClassIds ?? []).map(cls => cls.id);

  const hiddenClassIds = useHiddenClassIds(skip);
  const icons = useClassIcons(skip);

  const data = useLazyLoadQuery<ClassesGlobalFilterQuery>(
    globalClassFilterClasses,
    { skip },
    { fetchPolicy: classesVersion > 0 ? 'network-only' : 'store-or-network', fetchKey: classesVersion },
  );

  const classes = useMemo<GlobalFilterClass[]>(() => {
    const nodes = data.classes?.nodes ?? [];
    return nodes
      .filter((node): node is NonNullable<typeof node> => node !== null && !hiddenClassIds.has(node.id))
      .map(node => ({
        id: node.id,
        name: node.name ?? '',
        section: node.section ?? null,
        studentCount: (node.students ?? []).length,
        icon: resolveClassIcon(node.id, icons, CLASS_ICON_NAMES),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [data, hiddenClassIds, icons]);

  useEffect(() => {
    if (isHydrated || !publicUserId) return;

    const reconciled = reconcileGlobalClassFilter({
      storedClassIds: readStoredIds('classes', publicUserId),
      availableClassIds: classes.map(cls => cls.id),
      dashboardClassIds: dashboardClassIdsRef.current,
    });

    dispatch(hydrateSelectedClassIds(reconciled));
  }, [isHydrated, publicUserId, classes, dispatch]);

  const select = useCallback(
    (classIds: string[]) => {
      dispatch(setSelectedClassIds(classIds));
      writeStoredIds('classes', publicUserId, classIds);
    },
    [dispatch, publicUserId],
  );

  const selectedClasses = useMemo(() => {
    if (selectedClassIds.length === 0) return classes;
    const selected = new Set(selectedClassIds);
    return classes.filter(cls => selected.has(cls.id));
  }, [classes, selectedClassIds]);

  const selectedStudentCount = useMemo(
    () => selectedClasses.reduce((total, cls) => total + cls.studentCount, 0),
    [selectedClasses],
  );

  return { classes, selectedClassIds, selectedClasses, selectedStudentCount, select };
};
